/**
 * Saga video section.
 *
 * YouTube specifically is rebuilt on the OFFICIAL YouTube IFrame Player API
 * (https://www.youtube.com/iframe_api), not the shared theme's postMessage-
 * based Alpine.store('xVideo').externalLoad/sagaSeekExternalIframe path this
 * file used before. Two concrete problems with that path, found while
 * debugging why play/seek silently didn't work:
 *   1. externalLoad() sets `videoContainer.innerHTML` on the CLOSEST
 *      .external-video ancestor — which for this section's markup IS the
 *      root .saga-video__player element itself, wiping out the poster,
 *      chrome and play button it also contains, not just inserting an
 *      iframe alongside them.
 *   2. Its injected iframe carries Tailwind utility classes
 *      (top-1/2 -translate-y-1/2) baked into a JS template string. If those
 *      happen to be purged from the compiled CSS the iframe still renders;
 *      if they're NOT purged, that transform survives this section's own
 *      inset:0 override (a different CSS property, not overridden by it)
 *      and can shift the video's visible content out of the clipped
 *      16:9 box. Either way, betting layout correctness on which utility
 *      classes happen to survive a Tailwind purge pass isn't reliable.
 * The official API sidesteps both: YT.Player(el, {...}) replaces a plain
 * mount element with its own real iframe (nothing else in the DOM is
 * touched), and .playVideo()/.seekTo() are real method calls, not
 * hand-built postMessage payloads guessing at YouTube's wire protocol.
 *
 * Vimeo and the native <video> (settings.video) path are UNCHANGED, still
 * on the shared assets/video.js mechanism — this rebuild is scoped to the
 * thing that was actually reported broken.
 */
document.addEventListener('alpine:init', () => {
  const xVideo = Alpine.store('xVideo');
  if (!xVideo) return;

  if (!xVideo.sagaParseTimecode) {
    Object.assign(xVideo, {
      sagaParseTimecode(value) {
        const parts = String(value || '').trim().split(':');
        if (parts.length < 2 || parts.length > 3 || parts.some((p) => !/^\d+$/.test(p))) return null;
        const nums = parts.map(Number);
        return nums.length === 3 ? nums[0] * 3600 + nums[1] * 60 + nums[2] : nums[0] * 60 + nums[1];
      },

      // Still used for Vimeo, which has no equivalent same-origin-script
      // API here — the theme's own already-established postMessage path.
      sagaSeekExternalIframe(iframe, host, seconds) {
        if (!iframe || !iframe.contentWindow) return;
        const message =
          host === 'youtube'
            ? { event: 'command', func: 'seekTo', args: [seconds, true] }
            : { method: 'setCurrentTime', value: seconds };
        iframe.contentWindow.postMessage(JSON.stringify(message), '*');
      },
    });
  }

  // One shared loader for the YouTube IFrame API script, however many
  // saga-video sections (or other YouTube embeds) end up on the page.
  let ytApiPromise = null;
  function loadYouTubeApi() {
    if (ytApiPromise) return ytApiPromise;
    ytApiPromise = new Promise((resolve) => {
      if (window.YT && window.YT.Player) {
        resolve(window.YT);
        return;
      }
      const previousReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (typeof previousReady === 'function') previousReady();
        resolve(window.YT);
      };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
    });
    return ytApiPromise;
  }

  document.querySelectorAll('[data-saga-video-root]').forEach((root) => {
    const sectionId = root.dataset.sagaVideoPlayer;
    const isExternal = !!root.dataset.externalHost;
    const host = root.dataset.externalHost;
    const isYouTube = host === 'youtube';
    const playButton = root.querySelector('.saga-video__play');
    const chapters = document.querySelectorAll(`.saga-video--${sectionId} .saga-video__chapter`);

    const fireCustomerEvent = (name, data) => {
      const store = Alpine.store('xCustomerEvent');
      if (store) store.fire(name, root, data);
    };

    const markPlaying = () => root.classList.add('is-playing');

    // ---- YouTube: official Player API ----------------------------------
    let ytPlayer = null;
    let ytPendingSeek = null;

    function ensureYouTubePlayer(seekSeconds) {
      if (typeof seekSeconds === 'number') ytPendingSeek = seekSeconds;
      if (ytPlayer) {
        if (ytPendingSeek !== null && ytPlayer.seekTo) {
          ytPlayer.seekTo(ytPendingSeek, true);
          ytPendingSeek = null;
        }
        if (ytPlayer.playVideo) ytPlayer.playVideo();
        return;
      }
      markPlaying();
      // A fresh element for YT.Player to replace with its own iframe — the
      // poster/chrome stay in the DOM (just hidden via .is-playing in CSS),
      // nothing here removes or rewrites them.
      const mount = document.createElement('div');
      root.appendChild(mount);
      loadYouTubeApi().then((YT) => {
        ytPlayer = new YT.Player(mount, {
          videoId: root.dataset.externalId,
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 1,
            // Muted, same as this theme's own existing YouTube embeds
            // elsewhere — unmuted autoplay after an async API round-trip
            // isn't reliably treated as still tied to the original click by
            // every browser, muted autoplay always is. Real controls stay
            // on, so unmuting is one click away.
            mute: 1,
            playsinline: 1,
            rel: 0,
            modestbranding: 1,
          },
          events: {
            onReady(event) {
              event.target.playVideo();
              if (ytPendingSeek !== null) {
                event.target.seekTo(ytPendingSeek, true);
                ytPendingSeek = null;
              }
            },
          },
        });
      });
    }

    // ---- Vimeo / native <video>: unchanged existing mechanism ----------
    const startExternalVimeo = () => {
      if (root.querySelector('iframe.video')) return; // already loaded
      const id = root.dataset.externalId;
      const title = root.querySelector('.saga-video__foot-title')?.textContent || '';
      xVideo.externalLoad(root, host, id, false, title);
      markPlaying();
      const waitForIframe = () => {
        const iframe = root.querySelector('iframe.video');
        if (!iframe) {
          requestAnimationFrame(waitForIframe);
          return;
        }
        iframe.addEventListener('load', () => {
          setTimeout(() => xVideo.externalPostCommand(iframe, 'play'), 300);
        }, { once: true });
      };
      waitForIframe();
    };

    const startNative = () => {
      xVideo.togglePlay(root);
      markPlaying();
    };

    if (playButton) {
      playButton.addEventListener('click', () => {
        if (isYouTube) {
          ensureYouTubePlayer();
        } else if (isExternal) {
          startExternalVimeo();
        } else {
          startNative();
        }
        fireCustomerEvent('saga_video_play', { section_id: sectionId });
      });
    }

    if (!isExternal) {
      // Progress thresholds only make sense for the native element, which
      // exposes real timeupdate/duration.
      const fired = new Set();
      root.addEventListener('play', markPlaying, true);
      root.addEventListener('timeupdate', (event) => {
        const video = event.target;
        if (!video.duration) return;
        const percent = (video.currentTime / video.duration) * 100;
        [25, 50, 75, 100].forEach((threshold) => {
          if (percent >= threshold && !fired.has(threshold)) {
            fired.add(threshold);
            fireCustomerEvent('saga_video_progress', { section_id: sectionId, percent: threshold });
          }
        });
      }, true);
    }

    chapters.forEach((button) => {
      button.addEventListener('click', () => {
        const seconds = xVideo.sagaParseTimecode(button.dataset.timecode);
        if (seconds === null) return; // unparseable timecode — ignore rather than throw

        const label = button.querySelector('.saga-video__chapter-label')?.textContent || '';
        fireCustomerEvent('saga_video_chapter_click', { section_id: sectionId, label, timecode: button.dataset.timecode });

        if (isYouTube) {
          ensureYouTubePlayer(seconds);
        } else if (isExternal) {
          const iframe = root.querySelector('iframe.video');
          if (iframe) {
            xVideo.sagaSeekExternalIframe(iframe, host, seconds);
          } else {
            startExternalVimeo();
            const check = () => {
              const loaded = root.querySelector('iframe.video');
              if (loaded) {
                loaded.addEventListener('load', () => {
                  setTimeout(() => xVideo.sagaSeekExternalIframe(loaded, host, seconds), 300);
                });
              } else {
                setTimeout(check, 100);
              }
            };
            check();
          }
        } else {
          const video = root.querySelector('video.video');
          if (!video) return;
          const seekAndPlay = () => {
            video.currentTime = seconds;
            video.play();
          };
          if (video.readyState > 0) {
            seekAndPlay();
          } else {
            video.addEventListener('loadedmetadata', seekAndPlay, { once: true });
          }
          markPlaying();
        }
      });
    });
  });
});
